/**
 * Group-month panel rules: how uneven a group is, how to order its members, and
 * which over-utilized member could hand hours to which free one. All of it reads
 * the cells the grid already has. Nothing here moves hours; assigning happens on
 * the Solution's Pod.
 */
import type { CellState } from "@/lib/availability/cell-state";
import { isFreeState, isOverState } from "@/lib/availability/cell-state";
import type { ViewRow } from "./model";

export interface PossibleMove {
  from: { resourceId: string; name: string };
  to: { resourceId: string; name: string };
  /** The most that could move: the smaller of the hours over and the hours free. */
  hours: number;
}

function stateOf(row: ViewRow, month: string): CellState | undefined {
  return row.cells[month]?.state;
}

function netOf(row: ViewRow, month: string): number {
  return row.cells[month]?.netHours ?? 0;
}

/**
 * Pair each over member with a free member, most over first against most free
 * first, one receiver per giver. Hours are `min(over, free)`, rounded to whole hours.
 */
export function possibleMoves(members: readonly ViewRow[], month: string): PossibleMove[] {
  const givers = members
    .filter((row) => isOverState(stateOf(row, month) ?? "noData") && netOf(row, month) < 0)
    .sort((a, b) => netOf(a, month) - netOf(b, month) || a.name.localeCompare(b.name));
  const receivers = members
    .filter((row) => isFreeState(stateOf(row, month) ?? "noData") && netOf(row, month) > 0)
    .sort((a, b) => netOf(b, month) - netOf(a, month) || a.name.localeCompare(b.name));

  const moves: PossibleMove[] = [];
  const pairs = Math.min(givers.length, receivers.length);
  for (let i = 0; i < pairs; i++) {
    const giver = givers[i];
    const receiver = receivers[i];
    if (giver === undefined || receiver === undefined) continue;
    moves.push({
      from: { resourceId: giver.resourceId, name: giver.name },
      to: { resourceId: receiver.resourceId, name: receiver.name },
      hours: Math.round(Math.min(-netOf(giver, month), netOf(receiver, month))),
    });
  }
  return moves;
}

export interface GroupBalance {
  over: number;
  room: number;
}

/**
 * The "Uneven." warning: the group reads Fully booked, Some room or Available
 * overall, yet some members are over-utilized while others have room. Null when
 * the group is not uneven (including when it is itself Stretched or Over-utilized).
 */
export function unevenGroup(
  groupState: CellState,
  members: readonly ViewRow[],
  month: string,
): GroupBalance | null {
  if (groupState !== "fullyBooked" && groupState !== "someRoom" && groupState !== "available") {
    return null;
  }
  let over = 0;
  let room = 0;
  for (const row of members) {
    const state = stateOf(row, month);
    if (state === undefined) continue;
    if (isOverState(state)) over++;
    else if (isFreeState(state)) room++;
  }
  return over > 0 && room > 0 ? { over, room } : null;
}

function rank(state: CellState | undefined): number {
  if (state !== undefined && isOverState(state)) return 0;
  if (state === "unplanned" || state === "noData" || state === undefined) return 2;
  return 1;
}

/** Most over first, then most free; people with nothing to compare go last. */
export function sortMembers(members: readonly ViewRow[], month: string): ViewRow[] {
  return [...members].sort((a, b) => {
    const ra = rank(stateOf(a, month));
    const rb = rank(stateOf(b, month));
    if (ra !== rb) return ra - rb;
    const diff = ra === 0 ? netOf(a, month) - netOf(b, month) : netOf(b, month) - netOf(a, month);
    return diff !== 0 ? diff : a.name.localeCompare(b.name);
  });
}
